import { Box, Typography } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ProductService from "../../services/ProductService";
import { useGlobals } from "../../hooks/useGlobals";
import { Order } from "../../../lib/types/order";
import { Product } from "../../../lib/types/product";
import { ProductCollection } from "../../../lib/enums/product.enum";
import { CartItem } from "../../../lib/types/search";
import { serverApi } from "../../../lib/config";
import { sweetErrorHandling, sweetTopSmallSuccessAlert } from "../../../lib/sweetAlert";

const RECENT_ORDERS_COUNT = 5;
const RECOMMENDED_LIMIT = 3;

const COLLECTION_LABELS: Record<string, string> = {
  [ProductCollection.FICTION]: "Fiction",
  [ProductCollection.NON_FICTION]: "Non-fiction",
  [ProductCollection.ACADEMIC]: "Academic",
  [ProductCollection.KIDS]: "Kids",
  [ProductCollection.OTHER]: "Other",
};

function getCollectionLabel(collection: ProductCollection | undefined): string {
  return collection ? (COLLECTION_LABELS[collection] ?? collection) : "Other";
}

/** From recent 5 finished orders, find the collection the user tends to; return top collection or null. */
function getPreferredCollection(finishedOrders: Order[]): ProductCollection | null {
  const recentOrders = finishedOrders.slice(0, RECENT_ORDERS_COUNT);
  const counts: Record<string, number> = {};

  for (const order of recentOrders) {
    if (!order.productData?.length) continue;
    for (const p of order.productData) {
      const col = p.productCollection ?? ProductCollection.OTHER;
      counts[col] = (counts[col] ?? 0) + 1;
    }
  }

  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0] as ProductCollection;
}

interface RecommendedOrdersProps {
  finishedOrders: Order[];
  onAdd?: (item: CartItem) => void;
  onProductClick?: (productId: string) => void;
  compact?: boolean;
}

export default function RecommendedOrders({
  finishedOrders,
  onAdd,
  onProductClick,
  compact,
}: RecommendedOrdersProps) {
  const { authMember } = useGlobals();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());

  const preferredCollection = useMemo(
    () => getPreferredCollection(finishedOrders),
    [finishedOrders]
  );

  useEffect(() => {
    if (!preferredCollection) {
      setProducts([]);
      return;
    }
    setLoading(true);
    const productService = new ProductService();
    productService
      .getProducts({
        order: "createdAt",
        page: 1,
        limit: RECOMMENDED_LIMIT,
        productCollection: preferredCollection,
      })
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [preferredCollection]);

  useEffect(() => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      products.forEach((p) => {
        if (p?.meLiked?.some((m) => m.myFavorite)) next.add(p._id);
      });
      return next;
    });
  }, [products]);

  const handleLike = async (e: React.MouseEvent, productId: string) => {
    e.stopPropagation();
    if (!authMember) {
      await sweetErrorHandling({ message: "Please log in to like products." });
      return;
    }
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
    try {
      const productService = new ProductService();
      const updated = await productService.likeProduct(productId);
      await sweetTopSmallSuccessAlert("Success", 800);
      setProducts((prev) =>
        prev.map((p) => (p._id === productId ? updated : p))
      );
    } catch (err) {
      setLikedIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      console.log(err);
    }
  };

  const isLiked = (product: Product) =>
    likedIds.has(product._id) || (product?.meLiked?.some((m) => m.myFavorite) ?? false);

  if (!preferredCollection || (products.length === 0 && !loading)) return null;

  return (
    <Box className={`user-recommended-orders ${compact ? "user-recommended-orders--compact" : ""}`}>
      <div className="user-recommended-orders-header">
        <span className="user-recommended-orders-title">Recommended for you</span>
        <span className="user-recommended-orders-subtitle">
        </span>
      </div>
      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          Loading…
        </Typography>
      ) : (
        <div className="products-page products-browse">
          <div className="products-grid user-favourites-grid user-recommended-orders-grid">
            {products.map((product) => {
              const imageSrc = product.productImages?.[0]
                ? `${serverApi}/${product.productImages[0]}`
                : "/icons/noimage-list.svg";
              const collectionSlug = (product.productCollection ?? "other")
                .toLowerCase()
                .replace("_", "-");
              const productCollectionLabel = getCollectionLabel(product.productCollection);
              return (
                <article
                  key={product._id}
                  className={`product-card product-card-book product-card--${collectionSlug} user-favourite-card`}
                  onClick={() => onProductClick?.(product._id)}
                >
                  <div className="user-favourite-card-image-wrap">
                    <img
                      src={imageSrc}
                      alt={product.productName}
                      className="user-favourite-card-image"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/icons/noimage-list.svg";
                      }}
                    />
                    <div className={`user-favourite-card-badges user-favourite-card-badges--${collectionSlug}`}>
                      <span className="user-favourite-card-badge">
                        {productCollectionLabel.toUpperCase()}
                      </span>
                    </div>
                    {onAdd && (
                      <button
                        type="button"
                        className="user-favourite-card-add-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAdd({
                            _id: product._id,
                            quantity: 1,
                            name: product.productName,
                            price: product.productPrice,
                            image: product.productImages?.[0] ?? "",
                          });
                        }}
                      >
                        <AddShoppingCartIcon sx={{ fontSize: 18 }} />
                        Add to basket
                      </button>
                    )}
                  </div>
                  <Typography variant="subtitle1" className="product-card-title">
                    {product.productName}
                  </Typography>
                  {product.productAuthor && (
                    <Typography variant="body2" color="text.secondary" className="product-card-author">
                      {product.productAuthor}
                    </Typography>
                  )}
                  <div className="product-card-divider" />
                  <div className="product-card-footer">
                    <div className="product-card-footer-left">
                      <Typography variant="body1" className="product-card-price">
                        ${product.productPrice.toFixed(2)}
                      </Typography>
                    </div>
                    <div className="product-card-footer-divider" />
                    <div className="product-card-footer-stats">
                      <span className="product-card-views">
                        <RemoveRedEyeIcon sx={{ fontSize: 22 }} />
                        <span className="product-card-stat-num">{product.productViews ?? 0}</span>
                      </span>
                      <button
                        type="button"
                        className={`product-card-likes ${isLiked(product) ? "product-card-liked" : ""}`}
                        onClick={(e) => handleLike(e, product._id)}
                        aria-label={isLiked(product) ? "Unlike" : "Like"}
                      >
                        {isLiked(product) ? (
                          <FavoriteIcon sx={{ fontSize: 22, color: "#e53935" }} />
                        ) : (
                          <FavoriteBorderIcon sx={{ fontSize: 22 }} />
                        )}
                        <span className="product-card-stat-num">{product.productLikes ?? 0}</span>
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}
    </Box>
  );
}
